import React, { useState, useEffect } from 'react';

const TicketsScreen = ({ onNavigate, user, company }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [units, setUnits] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [reprinting, setReprinting] = useState({});
    const [voiding, setVoiding] = useState({});
    const [selectedDate, setSelectedDate] = useState(() => {
        // Use device's local time zone
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      });

    const [searchTerms, setSearchTerms] = useState({
        tickets: '',
    });

    const filteredTickets = tickets.filter(t =>
        (t?.TicketNum?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase()) ||
        (t?.Date?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase()) ||
        (t?.Time?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase()) ||
        (t?.RegisteredTruck?.NumberPlate?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase()) ||
        (t?.Customer?.Name?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase()) ||
        (t?.Product?.Name?.toLowerCase() || '').includes(searchTerms.tickets.toLowerCase())
    );

    // Fetch tickets for the selected date
    const fetchTickets = async (date) => {
        setLoading(true);
        setError(null);

        try {
            // console.log('🔍 Fetching tickets for date:', date);

            // Make API request to fetch tickets
            const response = await window.electronAPI.request(`/companies/${company.Id}/tickets?date=${date}&hideDeleted=true`, 'GET', null, {
                'Accept': 'application/json',
            });

            if (response.ok) {
                const ticketsData = response.body;
                // console.log('✅ Tickets fetched successfully:', ticketsData);
                setTickets(Array.isArray(ticketsData) ? ticketsData : []);
            } else {
                console.error('❌ Failed to fetch tickets:', response.statusText);
                setError(`Failed to fetch tickets: ${response.statusText}`);
                setTickets([]);
            }
        } catch (error) {
            console.error('❌ Error fetching tickets:', error);
            setError('Failed to fetch tickets. Please try again.');
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchUnits = async () => {
        try {
            setUnitsLoading(true);

            const unitsResponse = await window.electronAPI.request(
                `/units`,
                'GET',
                null,
                {
                    'Accept': 'application/json',
                }
            );

            if (unitsResponse.ok) {
                const unitsData = await unitsResponse.body;

                // Handle the case where the API returns { body: [...] }
                const actualUnits = unitsData.body || unitsData;
                setUnits(actualUnits);
            } else {
                console.error('❌ Failed to fetch units:', unitsResponse.statusText);
            }
        } catch (error) {
            console.error('❌ Error fetching units:', error);
        } finally {
            setUnitsLoading(false);
        }
    }

    const getUnitName = (unitId) => {
        if (!unitId || units.length === 0) return 'Loading...';
        const unit = units.find(u => u.Id === unitId);
        return unit ? unit.Name : 'Unknown Unit';
    };

    // Load tickets when component mounts or date changes
    useEffect(() => {
        fetchUnits();
        fetchTickets(selectedDate);
    }, [selectedDate]);

    // Handle reprint functionality
    const handleReprint = async (ticket) => {
        const ticketId = ticket.id || ticket.ticketNumber;
        setReprinting(prev => ({ ...prev, [ticketId]: true }));

        try {
            // console.log('🖨️ Reprinting ticket:', ticketId);

            // Get printer configuration
            const settings = await window.electronAPI.secureStorage.getSettings();
            const printerConfig = settings?.deviceConfig;

            const created = new Date(ticket.Created);
            const formattedDate = created.toLocaleDateString();
            const formattedTime = created.toLocaleTimeString();

            if (!printerConfig?.printer) {
                throw new Error('No printer configured');
            }

            // Prepare ticket data for printing
            const printData = {
                printerName: printerConfig.printer,
                paperWidth: printerConfig.paperWidth || '80mm',
                ticketNumber: ticket.TicketNum || ticket.Id,
                text: [
                    company?.Name || 'COMPANY NAME',
                    `${ticket.Product?.LocationName || ''}`,
                    `<br />`,
                    `Ticket #: ${ticket.TicketNum}`,
                    `<br />`,
                    `${formattedDate} ${formattedTime}`,
                    `<br />`,
                    `Product: ${ticket.Product.Name || 'N/A'}`,
                    `Quantity: ${ticket.Quantities[0]} ${getUnitName(ticket.Product.UnitId)}s`,
                    `<br />`,
                    `Customer: ${ticket.Customer.Name || 'N/A'}`,
                    `PO #: ${ticket.PONum?.trim() || 'N/A'}`,
                    `<br />`,
                    `Truck #: ${ticket.RegisteredTruck.TruckNumber || ticket.RegisteredTruck.NumberPlate || 'N/A'}`,
                    `License Plate: ${ticket.RegisteredTruck.NumberPlate || 'N/A'}`,
                    `<br />`,
                    `Tare Weight: ${state.tareWeight || 'N/A'}`,
                    `Gross Weight: ${state.currentWeight || 'N/A'}`,
                    `Net Weight: ${state.netWeight || 'N/A'}`,
                    `<br />`,
                    `<br />`,
                    '--- REPRINT ---',
                    `<br />`,
                    `<br />`
                ]
            };

            // Send print job
            await window.printerAPI.printJob(printData);

        } catch (error) {
            console.error('❌ Reprint failed:', error);
            alert(`Reprint failed: ${error.message}`);
        } finally {
            setReprinting(prev => ({ ...prev, [ticketId]: false }));
        }
    };

    // Handle void ticket functionality
    const handleVoidTicket = async (ticket) => {
        const ticketId = ticket.Id || ticket.TicketNum;
        
        // Show confirmation dialog
        const confirmVoid = window.confirm(
            `Are you sure you want to void this ticket?\n\n` +
            `Ticket #: ${ticket.TicketNum || ticketId}\n` +
            `Customer: ${ticket.Customer?.Name || 'N/A'}\n` +
            `Product: ${ticket.Product?.Name || 'N/A'}\n` +
            `Amount: ${ticket.Quantities?.[0] || 0} ${getUnitName(ticket.Product?.UnitId)}\n\n` +
            `This action cannot be undone.`
        );

        if (!confirmVoid) {
            return;
        }

        setVoiding(prev => ({ ...prev, [ticketId]: true }));

        try {
            // console.log('🗑️ Voiding ticket:', ticketId);

            // Make API request to void the ticket
            const response = await window.electronAPI.request(
                `/companies/${company.Id}/tickets/${ticketId}/delete`,
                'POST',
                {
                    'DeleteReason': 'Voided via TruckScalePro'
                },
                {
                    'Accept': 'application/json',
                }
            );

            if (response.ok) {
                // console.log('✅ Ticket voided successfully');

                // Show success message
                alert(`Ticket #${ticket.TicketNum || ticketId} has been voided successfully.`);

                // Refresh the tickets list to reflect the change
                await fetchTickets(selectedDate);

            } else {
                throw new Error(`Failed to void ticket: ${response.statusText}`);
            }

        } catch (error) {
            console.error('❌ Void ticket failed:', error);
            alert(`Failed to void ticket: ${error.message}`);
        } finally {
            setVoiding(prev => ({ ...prev, [ticketId]: false }));
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    // Format time for display
    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleTimeString();
    };

    // Format weight for display
    const formatWeight = (weight) => {
        if (!weight) return 'N/A';
        return weight.toLocaleString() + ' Tons';
    };

    // Calculate total weight for the day
    const totalWeight = tickets.reduce((sum, ticket) => sum + (ticket.netWeight || 0), 0);

    return (
        <div className="screen">
            <div className="screen-header">
                <div className="header-content">
                    <div className="header-left">
                        <button
                            className="btn btn-secondary"
                            onClick={() => onNavigate('home')}
                        >
                            <i className="fas fa-arrow-left"></i> Back to Home
                        </button>
                    </div>
                    <div className="header-center">
                        <h1>Daily Tickets</h1>
                        <p>View and reprint tickets for {formatDate(selectedDate)}</p>
                    </div>
                    <div className="header-right" style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
                        <div className="date-selector">
                            <label htmlFor="date-select" className="date-label">
                                Date:
                            </label>
                            <input
                                id="date-select"
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="form-input date-input"
                            />
                        </div>
                        
                        <div className="search-box" style={{ margin: '0' }}>
                            <input 
                                className="form-input" 
                                placeholder="Search tickets..."
                                value={searchTerms.tickets}
                                autoFocus
                                onChange={(e) => setSearchTerms({...searchTerms, tickets: e.target.value})}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="screen-content">
                {loading ? (
                    <div className="text-center" style={{ padding: '2rem' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p style={{ marginTop: '1rem' }}>Loading tickets...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger" style={{ margin: '2rem' }}>
                        <strong>Error:</strong> {error}
                        <button
                            className="btn btn-outline-danger btn-sm ml-2"
                            onClick={() => fetchTickets(selectedDate)}
                        >
                            Retry
                        </button>
                    </div>
                ) : filteredTickets.length === 0 ? (
                    <div className="text-center" style={{ padding: '2rem' }}>
                        <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
                        <h3>No Tickets Found</h3>
                        <p className="text-muted">No tickets were created on {formatDate(selectedDate)}</p>
                    </div>
                ) : (
                    <>
                        {/* Tickets Table */}
                        <div className="card" style={{
                            margin: '2rem 0'
                        }}>
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-table"></i> Tickets for {formatDate(selectedDate)}
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0" style={{
                                        width: '100%'
                                    }}>
                                        <thead className="thead-light" style={{ textAlign: 'left' }}>
                                            <tr>
                                                <th>Ticket #</th>
                                                <th>Time</th>
                                                <th>Truck</th>
                                                <th>Customer</th>
                                                <th>Product</th>
                                                <th>Tare</th>
                                                <th>Gross</th>
                                                <th>Net</th>
                                                <th style={{ width: '180px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTickets.map((ticket, index) => {
                                                const ticketId = ticket.Id || ticket.TicketNum;
                                                const isReprinting = reprinting[ticketId];
                                                const isVoiding = voiding[ticketId];

                                                return (
                                                    <tr key={ticketId || index}>
                                                        <td>
                                                            <strong>{ticket.TicketNum || ticket.Id || 'N/A'}</strong>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div>{formatTime(ticket.Created || ticket.Date)}</div>
                                                                <small className="text-muted">{formatDate(ticket.Created || ticket.Date)}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div>{ticket.RegisteredTruck.NumberPlate || 'N/A'}</div>
                                                                {ticket.TruckNumber && (
                                                                    <small className="text-muted">{ticket.TruckNumber}</small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div>{ticket.Customer.Name || 'N/A'}</div>
                                                                {ticket.Project?.Name && (
                                                                    <small className="text-muted">{ticket.Project.Name}</small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{ticket.Product.Name || 'N/A'}</td>
                                                        <td className="text-right">{formatWeight(ticket.RegisteredTruck.TareWeight)}</td>
                                                        <td className="text-right">{formatWeight((parseFloat(ticket.Quantities[0]) + parseFloat(ticket.RegisteredTruck.TareWeight)).toFixed(2))}</td>
                                                        <td className="text-right">
                                                            <strong className="text-success">{formatWeight(ticket.Quantities[0])}</strong>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                <button
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => handleReprint(ticket)}
                                                                    disabled={isReprinting || isVoiding}
                                                                    title="Reprint ticket"
                                                                    style={{ flex: '1' }}
                                                                >
                                                                    {isReprinting ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                                                                            Printing...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-print"></i> Reprint
                                                                        </>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleVoidTicket(ticket)}
                                                                    disabled={isVoiding || isReprinting}
                                                                    title="Void this ticket"
                                                                    style={{ flex: '1' }}
                                                                >
                                                                    {isVoiding ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                                                                            Voiding...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-trash"></i> Void
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TicketsScreen;